import SpectralisComparisonViewContext from "./spectralisComparisonViewContext";
import store from "../store";
import { EyeTypes, OCTBrowserType } from "../config/config";
import CirrusComparisonViewContext from "./cirrusComparisonViewContext";
import RasterComparisonViewContext from "./rasterComparisonViewContext";

export default class ComparisonContextBase {
  constructor(exam, eye, browserTypes, initialBrowserType, componentName, browserName, launchOption, compareImgKey) {
    this.componentName = componentName;
    this.browserName = browserName;

    this.isComparisonContext = true;

    this.ptSrNo = exam.ptSrNo;
    this.eye = eye;
    this.preferredBrowserType = initialBrowserType || browserTypes[0];
    this.browserType = browserTypes;

    this.exam = exam;

    this.baseline = null;
    this.compare = null;

    this.lockEnabled = true;
    this.isLocked = true;
    this.alignFoveas = true;
    this.hasFoveaAlignControl = false;

    this.osExams = store.getters.octExams.filter(e =>
      e.osGroups.some(g => g.recognized && browserTypes.includes(g.browserType)));

    this.odExams = store.getters.octExams.filter(e =>
      e.odGroups.some(g => g.recognized && browserTypes.includes(g.browserType)));

    this.launchOption = launchOption;
    this.compareImgKey = compareImgKey;
  }

  get exams() {
    return this.eye === EyeTypes.OD ? this.odExams : this.osExams;
  }

  get showFoveaAlignControl() {
    return this.hasFoveaAlignControl
      && this.compare && this.compare.browserType === OCTBrowserType.Cirrus
      && this.baseline && this.baseline.browserType === OCTBrowserType.Cirrus;
  }

  startupTask() {
    let groups = this.eye === EyeTypes.OD ? this.exam.odGroups : this.exam.osGroups;
    if(this.compareImgKey){
      let image = store.getters.imageByKey(this.compareImgKey);
      let group = groups.find(g => g.recognized && g.browserType === this.preferredBrowserType && g.groupName === image.imgGroup);
      return this.selectCompare(group, this.exam, this.compareImgKey);
    }
    else{
      let group = groups.find(g => g.recognized && g.browserType === this.preferredBrowserType);
      return this.selectCompare(group, this.exam);
    }
  }

  refreshImages(){
    return store.dispatch("loadOCTImagesForMultiview");
  }

  clearContext() {
    if (this.compare) this.compare.clearContext();
    if (this.baseline) this.baseline.clearContext();
  }

  selectBaseline(group, exam) {
    if (!group || !exam) {
      if (this.baseline) this.baseline.clearContext();
      this.baseline = null;
      return Promise.resolve();
    }
    if (this.baseline && this.baseline.group === group) return Promise.resolve();
    if (this.baseline) this.baseline.pause();

    this.baseline = this.generateViewContext(group, exam, "baseline");
    this.baseline.loadImages = this.reloadImages;
    this.lockIfAble();
    return this.baseline.startupTask()
      .then(() => {
        if (this.isLocked) return store.dispatch("synchronizeOCTCompare");
      });
  }
  
  selectCompare(group, exam, compareImgKey) {
    this.exam = exam;
    this.preferredBrowserType = group.browserType;
    this.compare = this.generateViewContext(group, exam, "compare", compareImgKey);
    this.compare.loadImages = this.reloadImages;
    this.lockIfAble();
    return Promise.all([this.compare.startupTask(), store.dispatch("tryGetBaselineContext", {launchOption: this.launchOption})]);
  }

  // based on the type of browser, select the closest view context
  generateViewContext(group, exam, view, compareImgKey) {
    switch (group.browserType) {
      case OCTBrowserType.Spectralis: return new SpectralisComparisonViewContext(group, exam, view);
      case OCTBrowserType.Cirrus: return new CirrusComparisonViewContext(group, exam, view);
      case OCTBrowserType.Raster: return new RasterComparisonViewContext(group, exam, view, compareImgKey);
      default: return null;
    }
  }

  setOCTImage(key, image) {
    if (this.compare && this.compare.exSrNo === image.exSrNo) this.compare.setOCTImage(key, image);
    else if (this.baseline && this.baseline.exSrNo === image.exSrNo) this.baseline.setOCTImage(key, image);
  }

  loadThicknessMap(image) {
    if (this.compare
      && this.compare.loadThicknessMap
      && this.compare.exSrNo === image.exSrNo) this.compare.loadThicknessMap();
    else if (this.baseline
      && this.baseline.loadThicknessMap
      && this.baseline.exSrNo === image.exSrNo) this.baseline.loadThicknessMap();
  }

  updateFoveaAlignment(val) {
    this.alignFoveas = val;
    this.calculateDifferenceMap();
  }

  calculateDifferenceMap() {
    if (this.compare && this.compare.differenceMapPalette && this.compare.calculateDifferenceMap)
    this.compare.calculateDifferenceMap(this.baseline, this.alignFoveas);
  }

  calculateDifferenceAnalysis() {
    if (this.compare && this.compare.calculateDifferenceAnalysis)
      this.compare.calculateDifferenceAnalysis(this.baseline);
  }

  lockIfAble() {
    this.lockEnabled = (this.compare && this.compare.browserType) === (this.baseline && this.baseline.browserType);
    this.isLocked = this.lockEnabled ? true : false;
  }

  toggleLocked() {
    this.isLocked = this.lockEnabled ? !this.isLocked : false;

    if (!this.lockEnabled)
      store.commit("displayStatus", { message: "Cannot lock exams - incompatible types", level: "warning" });

    // too many edge cases to consider when locking/unlocking while playback is happening
    // for consistent UI state, just puase
    if (this.baseline) this.baseline.pause();
    if (this.compare) this.compare.pause();
  }

  toggleSegmentationCompare(view) {
    this.callChildAction(view, "toggleSegmentation");
  }

  toggleMeasuringCompare(view) {
    this.callChildAction(view, "toggleMeasuring");
  }

  setMeasurementIndexCompare(x, view) {
    this.callChildAction(view, "setMeasurementIndex", x);
  }

  toggleColorCompare(view) {
    this.callChildAction(view, "toggleColorPalette");
  }

  toggleInvertedCompare(view) {
    this.callChildAction(view, "toggleInverted");
  }

  pauseCompare(view) {
    this.callChildAction(view, "pause");
  }

  setPlayRateCompare(view, rate) {
    this.callChildAction(view, "setPlayRate", rate);
  }

  setPlayIntervalCompare(view, intervalId) {
    this.callChildAction(view, "setPlayInterval", intervalId);
  }

  toggleOverlayCompare(view) {
    this.callChildAction(view, "toggleOverlay");
  }

  updateOverlayOpacityCompare(view, opacity) {
    this.callChildAction(view, "updateOverlayOpacity", opacity);
  }

  toggleETDRSCompare(view) {
    this.callChildAction(view, "toggleETDRS");
  }

  callChildAction(view, actionName, ...args) {
    if ((this.isLocked || view === "compare") && this.compare && this.compare[actionName])
      this.compare[actionName](...args);
    if ((this.isLocked || view === "baseline") && this.baseline && this.baseline[actionName])
      this.baseline[actionName](...args);
  }

  switchEye(eye) {
    this.eye = eye;
  }
}